/**
 * ops 依頼のフリーワード検索の語彙。検索ボックスの入力を検索トークンへ正規化する規則と、その
 * トークンがテキストに一致するかの判定をまとめる。
 *
 * `keywords.ts` の `deriveOpsKeywords` とは**別物**なので取り違えないこと。あちらは「長い依頼本文から
 * 重複候補を探す」ための抽出器で、テンプレ定型句を落とし漢字 2 字 / カタカナ 3 字以上の連なりだけを拾う。
 * 検索ボックスの入力（"ぽいんと" / "A" / "返品 遅延"）をそこへ通すと **0 件のキーワード**に落ちて
 * 「全件一致」に化けるため、検索にはこちらを使う。
 *
 * BQ ミラー側（SQL の述語）と Firestore 側（in-memory フィルタ）が **同じ規則**で絞るための単一 source。
 * 片方だけ違う一致規則にすると、同じ検索語で Firestore 由来の依頼だけ出る / 消えるという
 * 再現しにくい不整合になる。トークン化だけでなく **照合前の正規化（NFKC + 大小無視）もここで揃える**
 * ——BQ の `CONTAINS_SUBSTR` は NFKC 正規化を伴うため、JS 側で素の `includes` を使うと半角カナ /
 * 全角英数の依頼で両者の判定が食い違う。
 *
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 依頼のフリーワード検索の正規化規則。入力を空白区切りのトークンへ分解し、全トークンを含むものを一致とする（AND）。BQ の SQL 述語と Firestore の in-memory フィルタが同じ規則を共有する
 * @graph-connects none
 */
/** 1 クエリで使う最大トークン数（SQL パラメータの暴走防止）。 @graph-connects none */
export const OPS_SEARCH_MAX_TOKENS = 5;
/** 1 トークンの最大長（同上）。 @graph-connects none */
export const OPS_SEARCH_MAX_TOKEN_LENGTH = 64;
/**
 * 検索入力をトークンへ正規化する。半角 / 全角スペースで区切り、空要素を落とし、長さと件数に上限を掛ける。
 * 形態素解析はしない（recall 重視・日本語の分かち書き無しでも部分一致で拾えるため）。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business 検索ボックスの入力を空白区切りのトークンへ正規化する。空入力は空配列（＝絞り込みなし）
 * @graph-connects none
 */
export function deriveOpsSearchTokens(query) {
    // `\s` は U+3000（全角スペース）も含むので、区切りはこれ 1 本で足りる。
    return query
        .split(/\s+/u)
        .filter((t) => t !== '')
        .map((t) => t.slice(0, OPS_SEARCH_MAX_TOKEN_LENGTH))
        .slice(0, OPS_SEARCH_MAX_TOKENS);
}
/**
 * 照合用にテキストを正規化する。BQ の `CONTAINS_SUBSTR` は **NFKC 正規化 + 大小無視** で部分一致を
 * 判定するため、JS 側の照合も同じ前処理を通す。これを省くと、半角カナ / 全角英数（`ﾎﾟｲﾝﾄ` / `Ａ`）を
 * 含む依頼が BQ 側では一致するのに Firestore 側では一致せず、**依頼がどちらのストアにあるかで検索結果が
 * 変わる**（手入力のシート由来データでは半角 / 全角の混在は日常的に起きる）。
 * @graph-connects none
 */
function normalizeForMatch(text) {
    return text.normalize('NFKC').toLowerCase();
}
/**
 * 検索入力が ops No（番号）そのものかを判定する。`#412` / `412` / `aircloset:412` のいずれも
 * No 部分だけを取り出して返す（番号でなければ null）。番号検索は本文一致より意図が強いので、
 * 呼び出し側は No 完全一致を優先する。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business 検索入力が ops No（"#412" / "412" / "aircloset:412"）かを判定し、No 部分を返す。番号検索を本文検索より優先させるために使う
 * @graph-connects none
 */
export function parseOpsSearchNo(query) {
    // 全角数字（`４１２`）や全角 `＃` で打たれても番号として扱えるよう、判定前に NFKC で畳む。
    // BQ の No は半角数字なので、返す値も正規化後（半角）でなければ完全一致に使えない。
    const trimmed = normalizeForMatch(query).trim();
    // slug:No 形式は No 部分だけを見る（サービスは既存のサービス絞り込みが担当する）。
    const tail = trimmed.includes(':') ? trimmed.slice(trimmed.lastIndexOf(':') + 1) : trimmed;
    const no = tail.startsWith('#') ? tail.slice(1) : tail;
    return /^\d{1,10}$/u.test(no) ? no : null;
}
/**
 * テキストが全トークンを含むか（AND）を判定する。Firestore 側の in-memory フィルタ用で、BQ 側の
 * `CONTAINS_SUBSTR` 述語と同じ前処理（NFKC 正規化 + 大小無視）を通してから部分一致を見る。
 *
 * BQ の照合順序とビット単位で同一であることまでは保証しない（BQ 側の実装詳細に踏み込めないため）が、
 * 実データで差が出る主因である半角 / 全角の揺れはこれで吸収される。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business テキストが検索トークンを全て含むかを NFKC 正規化 + 大小無視で判定する（Firestore 側の絞り込み。BQ の CONTAINS_SUBSTR と前処理をそろえる）
 * @graph-connects normalizeForMatch [calls] 照合前の NFKC 正規化 + 小文字化
 */
export function opsSearchTextMatches(text, tokens) {
    if (tokens.length === 0)
        return true;
    const haystack = normalizeForMatch(text);
    return tokens.every((t) => haystack.includes(normalizeForMatch(t)));
}
//# sourceMappingURL=search.js.map